import { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon } from "lucide-react";
import TimeInput12 from "@/components/TimeInput12";
import { fmtDate } from "@/lib/format";

/**
 * RehearsalScheduleField — one button → one popover that sets the date,
 * start time, and end time together. Replaces three separate inputs so
 * nothing can overflow on narrow dialogs and the whole schedule lives in
 * one click.
 *
 * Props mirror the sibling TimeInput12 / DatePickerField for consistency:
 *   date      YYYY-MM-DD string
 *   startTime HH:MM 24h string
 *   endTime   HH:MM 24h string
 *   onChange  ({ date, startTime, endTime }) => void
 */
function parseYmd(s) {
  if (!s || typeof s !== "string") return undefined;
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}
function toYmd(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function toLabel(hhmm) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || ""));
  if (!m) return "—";
  const h24 = Number(m[1]);
  const min = m[2];
  const period = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 % 12 || 12;
  return `${h12}:${min} ${period}`;
}

export default function RehearsalScheduleField({
  date,
  startTime,
  endTime,
  onChange,
  testid = "rehearsal-schedule",
}) {
  const [open, setOpen] = useState(false);
  const dateObj = useMemo(() => parseYmd(date), [date]);
  const triggerLabel = useMemo(() => {
    if (!date) return "Pick date and time";
    const left = fmtDate(dateObj);
    const times = startTime || endTime ? `${toLabel(startTime)} – ${toLabel(endTime)}` : "Set times";
    return `${left} · ${times}`;
  }, [date, dateObj, startTime, endTime]);

  const setDate = (d) => onChange?.({ date: toYmd(d), startTime, endTime });
  const setStart = (v) => onChange?.({ date, startTime: v, endTime });
  const setEnd = (v) => onChange?.({ date, startTime, endTime: v });

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className="w-full h-11 px-3 rounded-md bg-[#121212] border border-white/10 hover:border-white/20 text-left text-sm inline-flex items-center gap-2 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold"
        >
          <CalendarIcon className="w-4 h-4 text-zinc-400 shrink-0" />
          <span className={date ? "text-white truncate" : "text-zinc-500 truncate"}>{triggerLabel}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto p-0 bg-[#121212] border-white/10 text-white z-[120]"
      >
        <div className="p-2">
          <Calendar
            mode="single"
            selected={dateObj}
            onSelect={(d) => d && setDate(d)}
            initialFocus
          />
        </div>
        <div className="px-3 pb-3 pt-1 border-t border-white/10 space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Call window</div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Start</div>
              <TimeInput12 value={startTime} onChange={setStart} testid={`${testid}-start`} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">End</div>
              <TimeInput12 value={endTime} onChange={setEnd} testid={`${testid}-end`} />
            </div>
          </div>
          <div className="flex items-center justify-between pt-1">
            {date ? (
              <button
                type="button"
                onClick={() => onChange?.({ date: "", startTime, endTime })}
                className="text-xs text-zinc-400 hover:text-white"
              >
                Clear date
              </button>
            ) : <span />}
            <button
              type="button"
              data-testid={`${testid}-done`}
              onClick={() => setOpen(false)}
              className="px-3 py-1 rounded bg-gold text-black text-xs font-semibold hover:brightness-110"
            >
              Done
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
